import React, { useMemo, useState } from "react";

import { getImportTaskFor } from "../tasks";
import { useBaseDataModelContext } from "../useBaseDataModel";

import { BOUNDARY_IMPORT_KEYS, BoundaryImportKey, isBoundaryImportKey } from "./boundary";
import ImportChecklist from "./ImportChecklist";
import { formatImportNouns } from "./importTask";
import TaskProgress, { TaskResult } from "./TaskProgress";
import { useImportWithinBoundary } from "./useImportWithinBoundary";

/** Layers ticked by default: aerial spans & poles are only imported when asked for. */
const DEFAULT_TICKED: Record<BoundaryImportKey, boolean> = { addresses: true, parcels: true, streets: true, aerial: false };

/**
 * The Boundary row's Import FOND base data option: a checklist of layers to import from inside the boundary.
 */
const BoundaryImport: React.FC = () => {
  const { rows, tasks, actions } = useBaseDataModelContext();
  const [ticked, setTicked] = useState(DEFAULT_TICKED);
  const tickedKeys = useMemo(() => BOUNDARY_IMPORT_KEYS.filter((key) => ticked[key]), [ticked]);
  const importWithinBoundary = useImportWithinBoundary("boundary");
  const task = getImportTaskFor(tasks, "boundary", tickedKeys);
  /** Whether each layer already has data that an import replaces. */
  const hasData = Object.fromEntries(BOUNDARY_IMPORT_KEYS.map((key) => [key, rows[key].hasData])) as Record<BoundaryImportKey, boolean>;
  const importNouns = formatImportNouns(task ? task.keys.filter(isBoundaryImportKey) : tickedKeys);

  if (task?.status === "failed") {
    return (
      <TaskProgress
        error={{
          title: `Couldn't import ${importNouns}`,
          reason: task.error ?? "",
          actions: [
            { label: "Cancel", onClick: () => actions.dismissTask("boundary") },
            { label: "Retry", onClick: () => importWithinBoundary(task.keys), primary: true },
          ],
        }}
      />
    );
  }

  if (task?.status === "running") return <TaskProgress title={`Importing ${importNouns}`} phase={task.phase} />;

  if (task?.status === "succeeded") return <TaskResult message="Base data imported" />;

  return (
    <ImportChecklist
      keys={BOUNDARY_IMPORT_KEYS}
      ticked={ticked}
      onToggle={(key) => setTicked((prev) => ({ ...prev, [key]: !prev[key] }))}
      hasData={hasData}
      onImport={() => importWithinBoundary(tickedKeys)}
    />
  );
};

export default BoundaryImport;
